import type { RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'

/**
 * "Has this entered the viewport yet?", answered once and then forgotten.
 *
 * Every animated explainer on the public site is driven from this rather than
 * from a scroll handler. An `IntersectionObserver` is evaluated off the main
 * thread and is disconnected the moment it fires, so a page carrying a dozen
 * sequenced diagrams still costs nothing while the visitor reads.
 *
 * REDUCED MOTION IS ANSWERED HERE, NOT AT THE CALL SITE. When the visitor asks
 * for less motion this reports `true` on the very first render, so every
 * dependent element mounts in its FINAL state. That is the whole contract: the
 * reduced path must reach the same end state, never a half-played one, and no
 * content may be gated behind an animation that will not run.
 *
 * It also reports `true` when `IntersectionObserver` is unavailable, so a
 * missing API degrades to "everything visible" rather than a blank section.
 */

interface RevealOptions {
    /** How much of the element must be showing. */
    threshold?: number
    /** Grow the viewport so a sequence starts just before it is scrolled to. */
    rootMargin?: string
}

export const prefersReducedMotion = (): boolean => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
)

export const useReveal = <T extends HTMLElement>(
    options: RevealOptions = {}
): { ref: RefObject<T | null>, shown: boolean } => {
    const { threshold = 0.25, rootMargin = '0px 0px -10% 0px' } = options

    const ref = useRef<T>(null)
    const [shown, setShown] = useState(false)

    useEffect(() => {
        /*
            Read the preference inside the effect rather than in `useState`'s
            initialiser: the server has no `matchMedia`, so deciding at render
            time would make the server and the browser disagree and React would
            throw a hydration mismatch. Mounting unshown and settling on the
            first client effect is the only form that is safe in both.
        */
        if (prefersReducedMotion()) {
            setShown(true)

            return
        }

        const node = ref.current

        if (!node || typeof IntersectionObserver === 'undefined') {
            setShown(true)

            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setShown(true)
                    /* Fires once. Nothing here re-animates on the way back up. */
                    observer.disconnect()
                }
            },
            { threshold, rootMargin }
        )

        observer.observe(node)

        return () => observer.disconnect()
    }, [threshold, rootMargin])

    return { ref, shown }
}
